// React hooks for risk data fetching
import { useState, useEffect } from 'react';
import { riskService } from '../services/riskService';

export function useProtocolRisks(protocolId) {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function fetchRisks() {
      if (!protocolId) {
        setRisks([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await riskService.getProtocolRisks(protocolId);
        setRisks(data);
        setError(null);
      } catch (err) {
        setError(err);
        console.error('Error fetching protocol risks:', err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchRisks();
  }, [protocolId]);
  
  const refresh = async () => {
    if (protocolId) {
      try {
        const data = await riskService.getProtocolRisks(protocolId);
        setRisks(data);
      } catch (err) {
        setError(err);
        console.error('Error refreshing protocol risks:', err);
      }
    }
  };
  
  return { risks, loading, error, refresh };
}

export function useStepRisks(stepId) {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function fetchRisks() {
      if (!stepId) {
        setRisks([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await riskService.getStepRisks(stepId);
        setRisks(data);
        setError(null);
      } catch (err) {
        setError(err);
        console.error('Error fetching step risks:', err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchRisks();
  }, [stepId]);
  
  const refresh = async () => {
    if (stepId) {
      try {
        const data = await riskService.getStepRisks(stepId);
        setRisks(data);
      } catch (err) {
        setError(err);
        console.error('Error refreshing step risks:', err);
      }
    }
  };
  
  return { risks, loading, error, refresh };
}

export function useActionRisks(actionId) {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function fetchRisks() {
      if (!actionId) {
        setRisks([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await riskService.getActionRisks(actionId);
        setRisks(data);
        setError(null);
      } catch (err) {
        setError(err);
        console.error('Error fetching action risks:', err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchRisks();
  }, [actionId]);
  
  const refresh = async () => {
    if (actionId) {
      try {
        const data = await riskService.getActionRisks(actionId);
        setRisks(data);
      } catch (err) {
        setError(err);
        console.error('Error refreshing action risks:', err);
      }
    }
  };
  
  return { risks, loading, error, refresh };
}

export function useRisk(riskId) {
  const [risk, setRisk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function fetchRisk() {
      if (!riskId) {
        setRisk(null);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await riskService.getRisk(riskId);
        setRisk(data);
        setError(null);
      } catch (err) {
        setError(err);
        console.error('Error fetching risk:', err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchRisk();
  }, [riskId]);
  
  const refresh = async () => {
    if (riskId) {
      try {
        const data = await riskService.getRisk(riskId);
        setRisk(data);
      } catch (err) {
        setError(err);
        console.error('Error refreshing risk:', err);
      }
    }
  };
  
  return { risk, loading, error, refresh };
}

export function useHighRiskAssociations(rpnThreshold = 100) {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function fetchHighRisks() {
      try {
        setLoading(true);
        const data = await riskService.getHighRiskAssociations(rpnThreshold);
        setRisks(data);
        setError(null);
      } catch (err) {
        setError(err);
        console.error('Error fetching high risk associations:', err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchHighRisks();
  }, [rpnThreshold]);
  
  const refresh = async () => {
    try {
      const data = await riskService.getHighRiskAssociations(rpnThreshold);
      setRisks(data);
    } catch (err) {
      setError(err);
      console.error('Error refreshing high risk associations:', err);
    }
  };
  
  return { risks, loading, error, refresh };
}

export function useRisksByCriticality(criticality) {
  const [risks, setRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    async function fetchRisks() {
      if (!criticality) {
        setRisks([]);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await riskService.getRisksByCriticality(criticality);
        setRisks(data);
        setError(null);
      } catch (err) {
        setError(err);
        console.error('Error fetching risks by criticality:', err);
      } finally {
        setLoading(false);
      }
    }
    
    fetchRisks();
  }, [criticality]);
  
  const refresh = async () => {
    if (criticality) {
      try {
        const data = await riskService.getRisksByCriticality(criticality);
        setRisks(data);
      } catch (err) {
        setError(err);
        console.error('Error refreshing risks by criticality:', err);
      }
    }
  };
  
  return { risks, loading, error, refresh };
}
